"use client";
import React, { useMemo, useState } from "react";
import { ReturnData, SchemeReturn } from "@/type/schemeType";
import RollingReturnsTable from "../others/RollingReturnsTable";
import RollingReturnChart from "../charts/RollingReturnChart";
import {
  ConsistencyResult,
  RollingFilledNavData,
  RollingNavReturns,
} from "@/type/rollingPageTypes";
import {
  calculateConsistency,
  calculateRollingReturns,
} from "@/utils/rollingReturnsCalculation";
import ConsistencyScore from "../others/ConsistencyScore";
import { getDateDiffFromNow } from "@/utils/commonFunctions";

type Periods = 1 | 3 | 5;

type RollingComputed = {
  fund?: RollingNavReturns[];
  benchmark?: RollingNavReturns[];
  all: RollingNavReturns[];
};

const EMPTY_RESULT: ConsistencyResult = {
  totalDays: 0,
  outperformDays: 0,
  underperformDays: 0,
  equalDays: 0,
  consistencyPercentage: "-",
};

export default function RollingTableAndChart({
  returnData,
  rollingData,
  rollingNavData,
}: {
  returnData: SchemeReturn;
  rollingData: ReturnData;
  rollingNavData: RollingFilledNavData[][];
}) {
  const [activePeriod, setActivePeriod] = useState<Periods>(1);
  const [isLoadingChart, setIsLoadingChart] = useState<boolean>(true);
  const [isLoadingScore, setIsLoadingScore] = useState<boolean>(true);
  const fundAge = getDateDiffFromNow({
    val: returnData.launch_date ?? null,
    format: "year",
    smallSuffix: false,
  });

  const rollingComputed = useMemo<RollingComputed>(() => {
    if (!rollingNavData.length) {
      return {
        fund: undefined,
        benchmark: undefined,
        all: [],
      };
    }
    setIsLoadingChart(true);
    setIsLoadingScore(true);

    let fund: RollingNavReturns[] | undefined;
    let benchmark: RollingNavReturns[] | undefined;

    const all: RollingNavReturns[] = [];

    for (let i = 0; i < rollingNavData.length; i++) {
      const schemeNav = rollingNavData[i];
      if (!schemeNav?.length) continue;

      const result = calculateRollingReturns(
        schemeNav,
        Number(activePeriod),
      ).returns;

      // Avoid flatMap → push manually (faster, less memory churn)
      for (let j = 0; j < result.length; j++) {
        all.push(result[j]);
      }

      // Capture first two separately for consistency calc
      if (i === 0) fund = result;
      else if (i === 1) benchmark = result;
    }
    setIsLoadingChart(false);
    return { fund, benchmark, all };
  }, [rollingNavData, activePeriod]);

  const consistencyScore = useMemo<ConsistencyResult>(() => {
    if (!rollingComputed.fund || !rollingComputed.benchmark) {
      return EMPTY_RESULT;
    }
    setIsLoadingScore(false);
    return calculateConsistency(
      rollingComputed.fund,
      rollingComputed.benchmark,
    );
  }, [rollingComputed]);

  // Memoize the Table Data (Static, uses props)
  const tableRollingData = useMemo(() => {
    if (!returnData || !rollingData) return [];
    return [
      {
        label: "This Fund",
        values: {
          "1Y": `${rollingData?.one_year_abs ?? "-"}`,
          "2Y": `${rollingData?.two_year ?? "-"}`,
          "3Y": `${rollingData?.three_year ?? "-"}`,
          "5Y": `${rollingData?.five_year ?? "-"}`,
        },
      },
      {
        label: "Category Average",
        values: {
          "1Y": `${returnData?.cr_one_year_abs ?? "-"}`,
          "2Y": `${returnData?.cr_two_year ?? "-"}`,
          "3Y": `${returnData?.cr_three_year ?? "-"}`,
          "5Y": `${returnData?.cr_five_year ?? "-"}`,
        },
      },
      {
        label: "Benchmark Returns",
        values: {
          "1Y": `${returnData?.br_one_year_abs ?? "-"}`,
          "2Y": `${returnData?.br_two_year ?? "-"}`,
          "3Y": `${returnData?.br_three_year ?? "-"}`,
          "5Y": `${returnData?.br_five_year ?? "-"}`,
        },
      },
    ];
  }, [returnData, rollingData]);

  if (!rollingData) return;

  return (
    <>
      <RollingReturnsTable data={tableRollingData} />

      {rollingComputed.fund && (
        <>
          <RollingReturnChart
            chartData={rollingComputed.fund}
            schemeName={returnData.scheme_name}
            fundAge={fundAge}
            activePeriod={activePeriod}
            onPeriodChange={setActivePeriod}
            isLoggedIn={true}
            isFetching={isLoadingChart}
          />

          <div className="flex flex-col sm:flex-row bg-[#F5F9FF] gap-0 sm:gap-7 items-center justify-between rounded-4xl px-6 py-4 lg:px-9">
            <div className="text-center sm:text-left w-full mb-4 sm:mb-0">
              <div className="font-semibold text-base sm:text-xl text-primaryBlack mb-1 sm:mb-3">
                Return Consistency Score
              </div>
              <p className="font-medium italic text-2xs sm:text-sm text-[#828282]">
                The consistency score is calculated by measuring how often the
                fund delivers better rolling returns than the benchmark over the
                same time periods.
              </p>
            </div>
            {isLoadingScore ? (
              <div className="h-31.5 w-54 bg-gray-200 shrink-0 animate-pulse rounded-xl" />
            ) : (
              <ConsistencyScore
                value={(
                  Number(consistencyScore.consistencyPercentage) || 100
                ).toFixed(0)}
                max={100}
              />
            )}
          </div>
        </>
      )}
    </>
  );
}
